<template>
  <v-dialog v-model="dialog" persistent max-width="900px">
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        class="lead-bottom-control-panel__btn"
        color="green darken-1"
        v-bind="attrs"
        v-on="on"
        :disabled="blocked"
      >
        <v-icon color="white">mdi-pencil</v-icon>
        <span class="lead-bottom-control-panel__item-text white--text">{{
          btnText
        }}</span>
      </v-btn>
    </template>
    <v-card>
      <v-card-title>
        <span class="headline">Client information</span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-form ref="formBase" v-model="valid" :lazy-validation="lazy">
            <h3 class="mt-3">Geo and Contact</h3>
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  validate-on-blur
                  counter="5"
                  v-mask="'#####'"
                  v-model="lead.zip"
                  label="Zip code"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  validate-on-blur
                  v-model="lead.county"
                  label="Country"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  validate-on-blur
                  v-model="lead.city"
                  label="City"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  validate-on-blur
                  v-model="lead.contact"
                  label="Contact name"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  validate-on-blur
                  v-model="lead.email"
                  label="Email"
                  :rules="emailRules"
                  type="email"
                  required
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  validate-on-blur
                  counter="13"
                  v-model="lead.phone"
                  v-mask="'(###)###-####'"
                  :rules="phoneValidate"
                  label="Phone"
                  required
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  validate-on-blur
                  counter="13"
                  v-model="lead.second_phone"
                  v-mask="'(###)###-####'"
                  :rules="phoneValidate"
                  label="Second Phone"
                />
              </v-col>
              <v-col cols="12" sm="7" md="3">
                <v-text-field
                  validate-on-blur
                  v-model="lead.address"
                  label="Address"
                />
              </v-col>
              <v-col cols="12" sm="7" md="3">
                <v-text-field
                  validate-on-blur
                  v-model="lead.address_2"
                  label="Address 2"
                />
              </v-col>
            </v-row>
            <h3 class="mt-3">Personal Information</h3>
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="lead.fullname"
                  :rules="emptyRules"
                  label="Full Name"
                  required
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  :rules="emptyRules"
                  v-model="lead.gender"
                  :items="baseData.gender"
                  item-text="name"
                  item-value="code"
                  menu-props="auto"
                  label="Gender"
                  required
                />
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-select
                  v-model="lead.height"
                  :items="baseData.heights"
                  item-text="text"
                  item-value="value"
                  menu-props="auto"
                  label="Height"
                />
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-text-field
                  v-mask="'###'"
                  v-model="lead.weight"
                  label="Pounds"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  :rules="emptyRules"
                  v-model="lead.birth_month"
                  :items="baseData.months"
                  item-text="name"
                  item-value="code"
                  menu-props="auto"
                  label="Month"
                  required
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  :rules="emptyRules"
                  v-mask="'##'"
                  v-model="lead.birth_day"
                  label="DD"
                  required
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  :rules="emptyRules"
                  v-mask="'####'"
                  v-model="lead.birth_year"
                  label="YYYY"
                  required
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="quote-details d-flex align-center">
                  <p class="toggle-text">Tobacco</p>
                  <div class="button r button-1">
                    <input
                      v-model="lead.tobacco"
                      :true-value="1"
                      :false-value="0"
                      type="checkbox"
                      class="checkbox"
                      required
                    />
                    <div class="knobs"></div>
                    <div class="layer"></div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="quote-details d-flex align-center">
                  <p class="toggle-text">Spouse</p>
                  <div class="button r button-1">
                    <input
                      v-model="is_spouse"
                      :true-value="1"
                      :false-value="0"
                      type="checkbox"
                      class="checkbox"
                      @change="changeIsSpouse"
                      required
                    />
                    <div class="knobs"></div>
                    <div class="layer"></div>
                  </div>
                </div>
              </v-col>
              <template v-if="is_spouse">
                <v-col cols="12" sm="12">
                  <h3>Spouse information</h3>
                </v-col>
                <ChildrenSpouseForm
                  :person="lead.spouse"
                  :emptyRules="emptyRules"
                  :baseData="baseData"
                  :index="1"
                  :type="'spouse'"
                />
              </template>
              <v-col cols="12" sm="6" md="3">
                <div class="quote-details d-flex align-center">
                  <p class="toggle-text">Children</p>
                  <div class="button r button-1">
                    <input
                      v-model="is_children"
                      :true-value="1"
                      :false-value="0"
                      type="checkbox"
                      class="checkbox"
                      @change="changeIsChildren"
                    />
                    <div class="knobs"></div>
                    <div class="layer"></div>
                  </div>
                </div>
              </v-col>
              <v-col
                class="d-flex align-center justify-space-around pb-9"
                cols="12"
                sm="4"
                v-if="is_children"
              >
                <p class="toggle-text">Children amount</p>
                <v-btn elevation="2" icon depressed small @click="popChild">
                  <v-icon dark> mdi-menu-down </v-icon>
                </v-btn>
                <h3 style="font-size: 20px">{{ lead.children.length }}</h3>
                <v-btn elevation="2" icon depressed small @click="pushChild">
                  <v-icon dark> mdi-menu-up </v-icon>
                </v-btn>
              </v-col>
              <template v-if="is_children">
                <v-col cols="12" sm="12">
                  <h3>Children information</h3>
                </v-col>
                <ChildrenSpouseForm
                  v-for="(child, index) in lead.children"
                  :person="child"
                  :key="index"
                  :emptyRules="emptyRules"
                  :baseData="baseData"
                  :index="index + 1"
                  :type="'child'"
                />
              </template>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  :rules="emptyRules"
                  v-model="lead.credit_rating"
                  :items="baseData.credit_rating"
                  item-text="name"
                  item-value="code"
                  menu-props="auto"
                  label="Credit Rating"
                  required
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="quote-details d-flex align-center">
                  <p class="toggle-text">Pregnant</p>
                  <div class="button r button-1">
                    <input
                      v-model="lead.pregnant"
                      :true-value="1"
                      :false-value="0"
                      type="checkbox"
                      class="checkbox"
                    />
                    <div class="knobs"></div>
                    <div class="layer"></div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="quote-details d-flex align-center">
                  <p class="toggle-text">Treated</p>
                  <div class="button r button-1">
                    <input
                      v-model="lead.treated"
                      :true-value="1"
                      :false-value="0"
                      type="checkbox"
                      class="checkbox"
                    />
                    <div class="knobs"></div>
                    <div class="layer"></div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="quote-details d-flex align-center">
                  <p class="toggle-text">Hospitalized</p>
                  <div class="button r button-1">
                    <input
                      v-model="lead.hospitalized"
                      :true-value="1"
                      :false-value="0"
                      type="checkbox"
                      class="checkbox"
                    />
                    <div class="knobs"></div>
                    <div class="layer"></div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="quote-details d-flex align-center">
                  <p class="toggle-text">Self Employed</p>
                  <div class="button r button-1">
                    <input
                      v-model="lead.self_employed"
                      :true-value="1"
                      :false-value="0"
                      type="checkbox"
                      class="checkbox"
                    />
                    <div class="knobs"></div>
                    <div class="layer"></div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="quote-details d-flex align-center">
                  <p class="toggle-text">Prescription medications</p>
                  <div class="button r button-1">
                    <input
                      v-model="is_medications"
                      :true-value="1"
                      :false-value="0"
                      type="checkbox"
                      class="checkbox"
                    />
                    <div class="knobs"></div>
                    <div class="layer"></div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="7" md="3" v-if="is_medications">
                <v-text-field
                  validate-on-blur
                  v-model="lead.prescription_medications"
                  label="Medications list"
                />
              </v-col>
              <v-col cols="12" sm="7" md="3">
                <v-text-field
                  validate-on-blur
                  v-model="lead.household_income"
                  label="Household Income"
                />
              </v-col>
              <v-col cols="12" sm="7" md="3">
                <v-text-field
                  validate-on-blur
                  v-model="lead.datavendor_id"
                  label="Datavendor ID"
                />
              </v-col>
              <v-col cols="12" sm="7" md="3">
                <v-text-field
                  validate-on-blur
                  v-model="lead.biz_type"
                  label="Biz. Type"
                />
              </v-col>
              <v-col cols="12" sm="7" md="3">
                <v-text-field
                  validate-on-blur
                  v-model="lead.biz_name"
                  label="Biz. Name"
                />
              </v-col>
            </v-row>
            <h3 class="mt-3">Major Conditions</h3>
            <v-row>
              <v-col cols="12" sm="6" md="5">
                <v-select
                  v-model="lead.medications"
                  :items="baseData.healthMedications"
                  item-text="name"
                  item-value="code"
                  label="Disease"
                  multiple
                >
                  <template v-slot:selection="{ index }">
                    <v-chip v-if="index === 0">
                      <span>{{
                        baseData.healthMedications.find(
                          (hm) => hm.code === lead.medications[0]
                        ).name
                      }}</span>
                    </v-chip>
                    <span v-if="index === 1" class="grey--text caption"
                      >(+{{ lead.medications.length - 1 }} others)</span
                    >
                  </template>
                </v-select>
              </v-col>
            </v-row>
            <h3 class="mt-3">Current Policy</h3>
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  :rules="emptyRules"
                  v-model="lead.qualifying_life_event"
                  :items="baseData.qualifying_life_event"
                  item-text="name"
                  item-value="code"
                  menu-props="auto"
                  label="Qualifying Life Event"
                  required
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="3"
                v-if="lead.qualifying_life_event !== 'none'"
              >
                <v-text-field
                  validate-on-blur
                  v-mask="'####-##-##'"
                  v-model="lead.qualifying_life_event_date"
                  label="Qualifying Life Event Date"
                  placeholder="YYYY-MM-DD"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="quote-details d-flex align-center">
                  <p class="toggle-text">Denied Coverage</p>
                  <div class="button r button-1">
                    <input
                      v-model="lead.denied_coverage"
                      :true-value="1"
                      :false-value="0"
                      type="checkbox"
                      class="checkbox"
                    />
                    <div class="knobs"></div>
                    <div class="layer"></div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="quote-details d-flex align-center">
                  <p class="toggle-text">Lost Coverage</p>
                  <div class="button r button-1">
                    <input
                      v-model="lead.lost_coverage"
                      :true-value="1"
                      :false-value="0"
                      type="checkbox"
                      class="checkbox"
                    />
                    <div class="knobs"></div>
                    <div class="layer"></div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="quote-details d-flex align-center">
                  <p class="toggle-text">Currently Insured</p>
                  <div class="button r button-1">
                    <input
                      v-model="lead.currently_insured"
                      :true-value="1"
                      :false-value="0"
                      type="checkbox"
                      class="checkbox"
                    />
                    <div class="knobs"></div>
                    <div class="layer"></div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="quote-details">
                  <p class="toggle-text">Chargeback</p>
                  <div class="button r button-1">
                    <input
                        v-model="lead.chargeback"
                        :true-value="1"
                        :false-value="0"
                        type="checkbox"
                        class="checkbox"
                    />
                    <div class="knobs"></div>
                    <div class="layer"></div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3" v-if="lead.currently_insured">
                <v-select
                  :rules="emptyRules"
                  v-model="lead.current_company"
                  :items="baseData.health_current_company"
                  item-text="name"
                  item-value="code"
                  menu-props="auto"
                  label="Current Company"
                  required
                  v-if="lead.currently_insured"
                />
              </v-col>
            </v-row>
            <h3 class="mt-3">Requested Coverage</h3>
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  :rules="emptyRules"
                  v-model="lead.coverage_type"
                  :items="baseData.coverage_type"
                  item-text="name"
                  item-value="code"
                  menu-props="auto"
                  label="Coverage Type"
                  required
                />
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="dialog = false">Close</v-btn>
        <template v-if="methods === 'update'">
          <v-btn
            :disabled="formIsValid"
            color="blue darken-1"
            text
            @click="update"
            >Update</v-btn
          >
        </template>
        <template v-else-if="methods === 'create'">
          <v-btn
            :disabled="formIsValid"
            color="blue darken-1"
            text
            @click="createNewLead"
            >Create</v-btn
          >
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import FormatService from "@/services/format.service.js";
import ChildrenSpouseForm from "@/components/forms/ChildrenSpouseForm";

export default {
  name: "HealthLeadMediaalphaForm",
  props: {
    lead: Object,
    baseData: Object,
    btnText: String,
    methods: String,
    type: String,
    blocked: Boolean,
  },
  components: {
    ChildrenSpouseForm,
  },
  data() {
    return {
      dialog: false,
      lazy: false,
      lazy2: false,
      valid: false,
      emailRules: [
        (v) => !!v || "E-mail is required",
        (v) =>
          /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i.test(
            v
          ) || "E-mail must be valid",
      ],
      emptyRules: [(v) => !!v || "This field is required"],
      phoneValidate: [
        (v) => !!v || "Phone is required",
        (v) =>
          v.toString().length === 13 ||
          "Please enter a valid 13-digit phone number",
      ],
      is_medications: 0,
      is_spouse: 0,
      is_children: 0,
    };
  },
  methods: {
    update() {
      this.dialog = false;
      let newCopyLeads = JSON.parse(JSON.stringify(this.lead));

      newCopyLeads.empty = 0;
      newCopyLeads.birth_date = `${newCopyLeads.birth_year}-${newCopyLeads.birth_month}-${newCopyLeads.birth_day}`;
      newCopyLeads.type = "health";

      if (this.lead.user_id) {
        newCopyLeads.agent = this.lead.user_id;
      }

      this.baseData.healthMedications.forEach((medication) => {
        if (newCopyLeads.medications.includes(medication.code)) {
          newCopyLeads[medication.code] = 1;
        } else {
          newCopyLeads[medication.code] = 0;
        }
      });

      if (!newCopyLeads.currently_insured) newCopyLeads.current_company = null;
      if (newCopyLeads.qualifying_life_event === "none")
        newCopyLeads.qualifying_life_event_date = null;
      if (!this.is_medications) newCopyLeads.prescription_medications = "";

      const formatedLead = FormatService.format(newCopyLeads);

      this.$socket.emit("process-lead", formatedLead);
    },
    pushChild() {
      this.lead.children.push({
        name: "",
        gender: "",
        height: null,
        weight: "",
        birth_date: "",
        tobacco: 0,
      });
    },
    popChild() {
      this.lead.children.pop();

      if (this.lead.children.length < 1) {
        this.is_children = false;
      }
    },
    changeIsChildren() {
      if (this.is_children) {
        this.pushChild();
      } else {
        this.lead.children = [];
      }
    },
    changeIsSpouse() {
      this.lead.spouse = this.is_spouse
        ? {
            name: "",
            gender: "",
            height: null,
            weight: "",
            birth_date: "",
            tobacco: 0,
          }
        : null;
    },
  },
  mounted() {
    if (this.lead.prescription_medications) this.is_medications = 1;

    if (this.lead.spouse) {
      if (Object.keys(this.lead.spouse).length) this.is_spouse = 1;
    }

    if (this.lead.children) {
      if (this.lead.children.length > 0) this.is_children = 1;
    }
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
    }),
    formIsValid: function () {
      return !this.valid;
    },
  },
};
</script>
